import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import {
  LoginForm,
  PasswordResetPage,
  EmailVerificationPage,
  InvitationAcceptancePage
} from '@/components/auth'
import SocialMediaDashboard from '@/components/social-media/SocialMediaDashboard'
import { NewsletterDashboard } from '@/components/newsletter'
import ReportsPage from '@/components/reports/ReportsPage'
import { SupportDashboard } from '@/components/support'
import BroadcastAdminPage from '@/components/broadcast/BroadcastAdminPage'
import ReferenceDataPage from '@/components/broadcast/ReferenceDataPage'
import PricingPage from '@/pages/PricingPage'
import RegistrationPage from '@/pages/RegistrationPage'
import EmailVerificationWaitingPage from '@/pages/onboarding/EmailVerificationWaitingPage'
import CheckoutPage from '@/pages/onboarding/CheckoutPage'
import TrialStartedPage from '@/pages/onboarding/TrialStartedPage'
import PaymentCanceledPage from '@/pages/onboarding/PaymentCanceledPage'
import TermsPage from '@/pages/TermsPage'
import PrivacyPage from '@/pages/PrivacyPage'
import SettingsPage from '@/pages/SettingsPage'
import NotFoundPage from '@/pages/NotFoundPage'
import SponsorshipsPage from '@/components/sponsorships/SponsorshipsPage'
import BrandScoutPage from '@/components/scout/BrandScoutPage'
import BrandDetailPage from '@/components/scout/BrandDetailPage'
import BroadcastsPage from '@/components/broadcast/customer/BroadcastsPage'
import BroadcastBrandsPage from '@/components/broadcast/customer/BroadcastBrandsPage'
import BroadcastDetailsPage from '@/components/broadcast/customer/BroadcastDetailsPage'
import BroadcastExposurePage from '@/components/broadcast/customer/BroadcastExposurePage'
import BroadcastBrandPage from '@/components/broadcast/customer/BroadcastBrandPage'
import BroadcastSponsorPage from '@/components/broadcast/customer/BroadcastSponsorPage'
import { RootRedirect } from './components/RootRedirect'
import { ProtectedLayoutWrapper } from './components/ProtectedLayoutWrapper'
import { PublicOnlyRoute } from './guards/PublicOnlyRoute'
import { ConditionalRoute } from './guards/ConditionalRoute'
import { RoleBasedRoute } from './guards/RoleBasedRoute'
import { canAccessSupportPage } from '@/utils/permissions'
import { ROUTES } from './routes'
import useGlobalCustomerStore from '@/stores/globalCustomerStore'

// Wrapper component for NewsletterDashboard to inject customer ID from store
const NewsletterDashboardWrapper = () => {
  const { selectedCustomer } = useGlobalCustomerStore()
  return <NewsletterDashboard customerId={selectedCustomer?.id || ''} />
}

/**
 * AppRouter - Main application router configuration
 *
 * Uses React Router v6's data router API (createBrowserRouter) for:
 * - Type-safe route definitions
 * - Error boundaries per route
 * - Nested route support
 * - Future data loading capabilities
 */
const router = createBrowserRouter([
  // Root - smart redirect based on auth state
  {
    path: ROUTES.HOME,
    element: <RootRedirect />,
    errorElement: <NotFoundPage />
  },

  // Public auth routes
  {
    path: ROUTES.LOGIN,
    element: (
      <PublicOnlyRoute>
        <LoginForm />
      </PublicOnlyRoute>
    )
  },
  {
    path: ROUTES.REGISTER,
    element: (
      <ConditionalRoute>
        <RegistrationPage />
      </ConditionalRoute>
    )
  },
  {
    path: ROUTES.PRICING,
    element: (
      <ConditionalRoute>
        <PricingPage />
      </ConditionalRoute>
    )
  },

  // Public pages (accessible regardless of auth state)
  {
    path: ROUTES.RESET_PASSWORD,
    element: <PasswordResetPage />
  },
  {
    path: ROUTES.VERIFY_EMAIL,
    element: <EmailVerificationPage />
  },
  {
    path: ROUTES.ACCEPT_INVITATION,
    element: <InvitationAcceptancePage />
  },
  {
    path: ROUTES.TERMS,
    element: <TermsPage />
  },
  {
    path: ROUTES.PRIVACY,
    element: <PrivacyPage />
  },

  // Onboarding routes (feature-flagged)
  {
    path: '/onboarding',
    children: [
      {
        path: 'verify-email',
        element: (
          <ConditionalRoute>
            <EmailVerificationWaitingPage />
          </ConditionalRoute>
        )
      },
      {
        path: 'checkout',
        element: (
          <ConditionalRoute>
            <CheckoutPage />
          </ConditionalRoute>
        )
      },
      {
        path: 'success',
        element: (
          <ConditionalRoute>
            <TrialStartedPage />
          </ConditionalRoute>
        )
      },
      {
        path: 'payment-canceled',
        element: (
          <ConditionalRoute>
            <PaymentCanceledPage />
          </ConditionalRoute>
        )
      }
    ]
  },

  // Protected routes with layout wrapper
  {
    path: '/',
    element: <ProtectedLayoutWrapper />,
    errorElement: <NotFoundPage />,
    children: [
      {
        path: 'social-media',
        element: <SocialMediaDashboard />
      },
      {
        path: 'newsletter',
        element: <NewsletterDashboardWrapper />
      },
      {
        path: 'reports',
        element: <ReportsPage />
      },
      {
        path: 'sponsorships',
        element: <SponsorshipsPage />
      },
      {
        path: 'brand-scout',
        element: <BrandScoutPage />
      },
      {
        path: 'broadcasts',
        element: <BroadcastsPage />
      },
      {
        // One sponsor across the whole library. The static segment outranks
        // broadcasts/:id/..., so it cannot be read as a broadcast id.
        path: 'broadcasts/sponsors/:partnerId',
        element: <BroadcastSponsorPage />
      },
      {
        path: 'broadcasts/:id',
        element: <BroadcastBrandsPage />
      },
      {
        path: 'broadcasts/:id/details',
        element: <BroadcastDetailsPage />
      },
      {
        path: 'broadcasts/:id/exposure',
        element: <BroadcastExposurePage />
      },
      {
        // One brand on one broadcast. A page rather than a drawer because
        // the appearance list is long and the frame must stay in view while
        // it is scrolled.
        path: 'broadcasts/:id/brands/:brandKey',
        element: <BroadcastBrandPage />
      },
      {
        path: 'brand-scout/:brandId',
        element: <BrandDetailPage />
      },
      {
        // Redirect old /inventory route to /sponsorships
        path: 'inventory',
        element: <Navigate to="/sponsorships" replace />
      },
      {
        // Redirect old /partners route to /sponsorships
        path: 'partners',
        element: <Navigate to="/sponsorships" replace />
      },
      {
        path: 'settings',
        element: <SettingsPage />
      },
      {
        path: 'support',
        element: (
          <RoleBasedRoute checkAccess={canAccessSupportPage}>
            <SupportDashboard />
          </RoleBasedRoute>
        )
      },
      {
        // Broadcast admin for ONE customer. The id is in the route rather than
        // in selected-customer state so a refresh keeps context and the link is
        // shareable.
        path: 'support/broadcast/:customerId',
        element: (
          <RoleBasedRoute checkAccess={canAccessSupportPage}>
            <BroadcastAdminPage />
          </RoleBasedRoute>
        )
      },
      {
        // Platform-wide lookups. Deliberately NOT nested under a customer —
        // changes here affect every customer.
        path: 'support/reference',
        element: (
          <RoleBasedRoute checkAccess={canAccessSupportPage}>
            <ReferenceDataPage />
          </RoleBasedRoute>
        )
      }
    ]
  },

  // 404 catch-all
  {
    path: '*',
    element: <NotFoundPage />
  }
])

/**
 * AppRouter Component
 *
 * Provides the RouterProvider with the configured router.
 * This component should be used in App.tsx within the provider stack.
 */
export function AppRouter() {
  return <RouterProvider router={router} />
}
